import type { StateCreator } from 'zustand';
import type { Trip, Day, TransportMode, TripParticipant, TripPreferences, CardTag, PlaceRef, TripCategory } from '@my-fullstack-app/itinerary-types';
import { DEFAULT_TRIP_CATEGORY_NAMES, migrateTripPlaces } from '@my-fullstack-app/itinerary-types';
import { generateId, createEmptyDays, sortByStartTime } from '../helpers';
import { compareIsoDates, normalizeIsoDate } from '../../lib/dates';
import { attributeOrphanCosts, migrateTripCosts, migrateTripParticipants, pruneTripCosts } from '../../lib/cost-migration';
import type { ItineraryState } from '../itinerary-store';
import { markTripDirty, markTripNew, markTripDeleted } from '../sync-bridge';
import type { TripField } from '../../lib/sync-manager';
import { track } from '../../lib/analytics';

export type TripRole = 'owner' | 'admin' | 'editor' | 'viewer';

export interface TripSlice {
  trips: Trip[];
  activeTripId: string | null;
  activeTripRole: TripRole | null;
  createTrip: (params: {
    name: string;
    destination: string;
    /** Optional picked destination snapshot — see ADR-0054. Filled by
     *  the new-trip place picker; absent for legacy free-text trips. */
    destinationPlace?: PlaceRef | undefined;
    startDate: string;
    endDate: string;
    preferences?: TripPreferences | undefined;
    /** When provided, seed `participants[]` with the trip creator —
     *  userId pre-linked so the cost editor can default `paidBy` to
     *  them. Solo trips work out of the box: every cost lands with
     *  the user as payer, totals show up immediately. */
    owner?: {
      userId: string;
      displayName: string;
      avatarUrl?: string | null | undefined;
    } | undefined;
  }) => Trip;
  updateTrip: (
    tripId: string,
    params: Partial<Pick<Trip, 'name' | 'destination' | 'startDate' | 'endDate' | 'preferences' | 'heroPlaceId'>>
  ) => void;
  deleteTrip: (tripId: string) => void;
  duplicateTrip: (tripId: string) => Trip | null;
  setActiveTrip: (tripId: string | null) => void;
  setActiveTripRole: (role: TripRole | null) => void;
  upsertTrip: (trip: Trip) => void;
  getTrip: (tripId: string) => Trip | undefined;
  importTrips: (
    trips: Trip[],
    opts?: { notes?: Record<string, string>; tags?: Record<string, CardTag> } | undefined,
  ) => void;
}

export const createTripSlice: StateCreator<ItineraryState, [], [], TripSlice> = (set, get) => ({
  trips: [],
  activeTripId: null,
  activeTripRole: null,

  createTrip: (params) => {
    const days = createEmptyDays(params.startDate, params.endDate);
    // ADR-0058: seed the default spending buckets at trip-create time
    // so phase 3's cost-entry form has something to attribute against
    // immediately. The user can rename / delete / recolour any of
    // them right after the trip lands.
    const categories: TripCategory[] = DEFAULT_TRIP_CATEGORY_NAMES.map((name, idx) => ({
      id: generateId(),
      name,
      colorIndex: idx % 4, // 4-entry palette today; cycles cleanly
    }));
    // Seed the creator as a participant when caller supplies one.
    // Pre-linked via userId so the cost editor (CostItemsEditor) can
    // pick them as the default payer. displayName is clamped to the
    // schema's 1..120-char window — falls back to a non-empty placeholder
    // if the auth profile happens to carry an empty name.
    const participants: TripParticipant[] = [];
    if (params.owner) {
      const ownerName = params.owner.displayName.trim().slice(0, 120) || 'Me';
      participants.push({
        id: generateId(),
        displayName: ownerName,
        userId: params.owner.userId,
        ...(params.owner.avatarUrl ? { avatarUrl: params.owner.avatarUrl } : {}),
      });
    }
    const trip: Trip = {
      id: generateId(),
      name: params.name,
      destination: params.destination,
      ...(params.destinationPlace ? { destinationPlace: params.destinationPlace } : {}),
      startDate: params.startDate,
      endDate: params.endDate,
      ...(params.preferences ? { preferences: params.preferences } : {}),
      days,
      categories,
      ...(participants.length > 0 ? { participants } : {}),
    };
    set((s) => ({
      trips: [...s.trips, trip],
      activeTripId: trip.id,
      activeTripRole: 'owner',
    }));
    markTripNew(trip.id);
    track('trip.created', {
      tripId: trip.id,
      dayCount: trip.days.length,
      hasDestinationPlace: !!trip.destinationPlace,
    });
    return trip;
  },

  updateTrip: (tripId, params) => {
    const oldDayCount = get().trips.find((t) => t.id === tripId)?.days.length ?? 0;
    set((s) => {
      const trip = s.trips.find((t) => t.id === tripId);
      if (!trip) return s;

      const updated: Trip = { ...trip, ...params };

      if (params.startDate != null || params.endDate != null) {
        const newStart = params.startDate ?? trip.startDate;
        const newEnd = params.endDate ?? trip.endDate;
        const rangeChanged = newStart !== trip.startDate || newEnd !== trip.endDate;
        if (rangeChanged || trip.days.length === 0) {
          const newDays = createEmptyDays(newStart, newEnd);
          if (rangeChanged && trip.days.length > 0 && newDays.length > 0) {
            const oldByDate = new Map(trip.days.map((d) => [d.date, d]));
            for (let i = 0; i < newDays.length; i++) {
              const oldDay = oldByDate.get(newDays[i]!.date);
              if (oldDay) {
                newDays[i] = {
                  ...newDays[i]!,
                  id: oldDay.id,
                  places: [...oldDay.places],
                  transports: [...(oldDay.transports ?? [])],
                  customs: [...(oldDay.customs ?? [])],
                  ...(oldDay.label !== undefined ? { label: oldDay.label } : {}),
                  ...(oldDay.hours !== undefined ? { hours: oldDay.hours } : {}),
                  ...(oldDay.notes !== undefined ? { notes: oldDay.notes } : {}),
                  ...(oldDay.autoTransportModeOverrides !== undefined
                    ? { autoTransportModeOverrides: oldDay.autoTransportModeOverrides }
                    : {}),
                };
              }
            }
            const endMovedEarlier =
              newEnd !== trip.endDate &&
              compareIsoDates(normalizeIsoDate(newEnd), normalizeIsoDate(trip.endDate)) < 0;
            if (endMovedEarlier) {
              const droppedDays = trip.days.filter(
                (d) => compareIsoDates(normalizeIsoDate(d.date), normalizeIsoDate(newEnd)) > 0
              );
              if (droppedDays.length > 0) {
                const lastIdx = newDays.length - 1;
                const extraPlaces = droppedDays.flatMap((d) => d.places);
                const extraTransports = droppedDays.flatMap((d) => d.transports ?? []);
                const extraCustoms = droppedDays.flatMap((d) => d.customs ?? []);
                newDays[lastIdx] = {
                  ...newDays[lastIdx]!,
                  places: sortByStartTime([...newDays[lastIdx]!.places, ...extraPlaces]),
                  transports: sortByStartTime([
                    ...(newDays[lastIdx]!.transports ?? []),
                    ...extraTransports,
                  ]),
                  customs: sortByStartTime([
                    ...(newDays[lastIdx]!.customs ?? []),
                    ...extraCustoms,
                  ]),
                  autoTransportModeOverrides: {
                    ...(newDays[lastIdx]!.autoTransportModeOverrides ?? {}),
                    ...Object.fromEntries(
                      droppedDays.flatMap((d) =>
                        Object.entries(d.autoTransportModeOverrides ?? {})
                      )
                    ),
                  },
                };
              }
            }
          }
          updated.days = newDays;
        }
      }

      return {
        trips: s.trips.map((t) => (t.id === tripId ? updated : t)),
      };
    });
    const changedFields: TripField[] = [];
    if (params.name != null) changedFields.push('name');
    if (params.destination != null) changedFields.push('destination');
    if (params.startDate != null) changedFields.push('startDate');
    if (params.endDate != null) changedFields.push('endDate');
    if (params.preferences != null) changedFields.push('preferences');
    if (params.heroPlaceId !== undefined) changedFields.push('heroPlaceId');
    if (params.startDate != null || params.endDate != null) changedFields.push('days');
    if (changedFields.length > 0) markTripDirty(tripId, changedFields);
    const newDayCount = get().trips.find((t) => t.id === tripId)?.days.length ?? 0;
    if (newDayCount > oldDayCount) {
      track('day.added', {
        tripId,
        added: newDayCount - oldDayCount,
        totalDays: newDayCount,
      });
    }
  },

  deleteTrip: (tripId) => {
    // Capture isDemo BEFORE mutation — markTripDeleted runs after the
    // trip is gone from state, so we can't re-check the flag later.
    // Demo trips are local-only; queuing a delete would try to remove a
    // record the server never knew about.
    const wasDemo = get().trips.find((t) => t.id === tripId)?.isDemo === true;
    set((s) => ({
      trips: s.trips.filter((t) => t.id !== tripId),
      activeTripId: s.activeTripId === tripId ? null : s.activeTripId,
    }));
    if (!wasDemo) markTripDeleted(tripId);
    track('trip.deleted', { tripId });
  },

  duplicateTrip: (tripId) => {
    const source = get().trips.find((t) => t.id === tripId);
    if (!source) return null;
    // Day-scoped id remap (days, places, transports, customs). Journey
    // / stay / participant / category ids are remapped separately so
    // duplicated trips can't accidentally cross-reference each other.
    // Cost line items live inline on Place / CustomEntry / Booking and
    // ride along with the parent entity automatically — no separate
    // remap pass needed (ADR-0069 superseded ADR-0059's sidecar).
    // `paidBy` / `splitWith` / `categoryId` on each line item still
    // need participant / category id remapping; we walk the costs
    // array per entity as we clone it.
    const idMap = new Map<string, string>();
    const remap = (id: string) => {
      const existing = idMap.get(id);
      if (existing) return existing;
      const next = generateId();
      idMap.set(id, next);
      return next;
    };
    const participantIdMap = new Map<string, string>();
    const remappedParticipants = source.participants?.map((p) => {
      const newId = generateId();
      participantIdMap.set(p.id, newId);
      return { ...p, id: newId };
    });
    const categoryIdMap = new Map<string, string>();
    const remappedCategories = source.categories?.map((c) => {
      const newId = generateId();
      categoryIdMap.set(c.id, newId);
      return { ...c, id: newId };
    });
    const remapCostItems = (items: import('@my-fullstack-app/itinerary-types').CostLineItem[] | undefined) => {
      if (!items) return undefined;
      return items.map((item) => {
        const next = { ...item, id: generateId() };
        if (item.paidBy !== undefined) {
          const mapped = participantIdMap.get(item.paidBy);
          if (mapped) next.paidBy = mapped;
          else delete (next as { paidBy?: string }).paidBy;
        }
        if (item.splitWith !== undefined) {
          next.splitWith = item.splitWith
            .map((id) => participantIdMap.get(id))
            .filter((id): id is string => !!id);
        }
        if (item.categoryId !== undefined) {
          const mapped = categoryIdMap.get(item.categoryId);
          if (mapped) next.categoryId = mapped;
          else delete (next as { categoryId?: string }).categoryId;
        }
        return next;
      });
    };
    const clonedDays: Day[] = source.days.map((day) => {
      const newDayId = remap(day.id);
      const remapOverrides = (overrides?: Record<string, TransportMode> | undefined): Record<string, TransportMode> | undefined => {
        if (!overrides) return undefined;
        const out: Record<string, TransportMode> = {};
        for (const [key, mode] of Object.entries(overrides)) {
          const [fromId, toId] = key.split('-');
          out[`${remap(fromId!)}-${remap(toId!)}`] = mode;
        }
        return out;
      };
      const remappedOverrides = remapOverrides(day.autoTransportModeOverrides);
      return {
        ...day,
        id: newDayId,
        places: day.places.map((p) => {
          const costs = remapCostItems(p.costs);
          return { ...p, id: remap(p.id), ...(costs ? { costs } : {}) };
        }),
        transports: (day.transports ?? []).map((tr) => ({ ...tr, id: remap(tr.id) })),
        customs: (day.customs ?? []).map((c) => {
          const costs = remapCostItems(c.costs);
          return { ...c, id: remap(c.id), ...(costs ? { costs } : {}) };
        }),
        ...(remappedOverrides !== undefined
          ? { autoTransportModeOverrides: remappedOverrides }
          : {}),
      };
    });
    const remappedJourneys = source.journeys?.map((j) => {
      const next = { ...j, id: generateId() };
      if (j.booking) {
        const costs = remapCostItems(j.booking.costs);
        next.booking = { ...j.booking, ...(costs ? { costs } : {}) };
      }
      return next;
    });
    const remappedStays = source.stays?.map((s) => {
      const next = { ...s, id: generateId() };
      if (s.booking) {
        const costs = remapCostItems(s.booking.costs);
        next.booking = { ...s.booking, ...(costs ? { costs } : {}) };
      }
      return next;
    });
    // Drop `isDemo` on the duplicate — duplicating the sample is the
    // canonical way to turn the demo into a real, syncable trip.
    const { isDemo: _isDemo, ...sourceWithoutDemo } = source;
    const copy: Trip = {
      ...sourceWithoutDemo,
      id: generateId(),
      name: `Copy of ${source.name}`,
      days: clonedDays,
      ...(remappedJourneys !== undefined ? { journeys: remappedJourneys } : {}),
      ...(remappedStays !== undefined ? { stays: remappedStays } : {}),
      ...(remappedParticipants !== undefined ? { participants: remappedParticipants } : {}),
      ...(remappedCategories !== undefined ? { categories: remappedCategories } : {}),
    };
    set((s) => ({ trips: [...s.trips, copy] }));
    markTripNew(copy.id);
    track('trip.duplicated', {
      sourceTripId: tripId,
      tripId: copy.id,
      dayCount: copy.days.length,
      hasJourneys: (copy.journeys?.length ?? 0) > 0,
      hasStays: (copy.stays?.length ?? 0) > 0,
    });
    return copy;
  },

  setActiveTrip: (tripId) => {
    set({ activeTripId: tripId });
  },

  setActiveTripRole: (role) => {
    set({ activeTripRole: role });
  },

  upsertTrip: (trip) => {
    // ADR-0055: migrate legacy stays/journeys to the unified PlaceRef
    // shape before anything downstream sees them. Idempotent.
    // ADR-0069: fold legacy single-cost / pricePaid / costAttribution
    // into per-line-item `costs[]`, then prune stale paidBy /
    // splitWith / categoryId refs across all items. Both idempotent
    // — returns the same trip reference when nothing changed.
    let migrated = pruneTripCosts(migrateTripCosts(migrateTripParticipants(migrateTripPlaces(trip))));
    // Solo-trip hydrate: when a trip lands with exactly one participant
    // (cross-device sync, shared-link pickup, demo import), attribute
    // any orphan cost items to that lone participant. They're the only
    // payer the data could mean. Multi-participant trips don't get
    // auto-attribution — there's no natural default — so the reader's
    // skip-orphan behavior in `costByParticipant` still applies there.
    if (migrated.participants?.length === 1) {
      migrated = attributeOrphanCosts(migrated, migrated.participants[0]!.id);
    }
    set((s) => {
      const exists = s.trips.some((t) => t.id === migrated.id);
      return {
        trips: exists
          ? s.trips.map((t) => (t.id === migrated.id ? migrated : t))
          : [...s.trips, migrated],
      };
    });
  },

  getTrip: (tripId) => {
    return get().trips.find((t) => t.id === tripId);
  },

  importTrips: (trips, opts) => {
    const migratedTrips = trips.map((t) => pruneTripCosts(migrateTripCosts(migrateTripParticipants(migrateTripPlaces(t)))));
    set((s) => {
      const importedNotes = opts?.notes;
      const importedTags = opts?.tags;
      return {
        trips: [...s.trips, ...migratedTrips],
        // Spread imported under local edits so anything the user has already
        // typed wins on a key collision (same shape as hydrateFromServer).
        ...(importedNotes && Object.keys(importedNotes).length > 0
          ? { notesMap: { ...importedNotes, ...s.notesMap } }
          : {}),
        ...(importedTags && Object.keys(importedTags).length > 0
          ? { tagsMap: { ...importedTags, ...s.tagsMap } }
          : {}),
      };
    });
    for (const trip of trips) {
      // Demo trips are local-only — skipping markTripNew keeps the sample
      // out of dirtyTrips so SyncManager never tries to upload it.
      if (trip.isDemo) continue;
      markTripNew(trip.id);
    }
  },
});
